// frontend/src/components/AdminTeamDetail/AdminTeamDetail.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, adminService } from '../../services/supabaseClient';
import toast, { Toaster } from 'react-hot-toast';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Team configurations (same as dashboard)
import FlagDisplay from '../FlagDisplay';
import TeamSwitcher from '../TeamSwitcher';

// Extracted modular components
import ProgressOverlay from './ProgressOverlay';
import LightboxModal from './LightboxModal';
import { useAuth } from '../../contexts/AuthProvider';
import AddPlayerModal from './AddPlayerModal';
import PlayerLinkModal from './PlayerLinkModal';
import ImportConfirmModal from './ImportConfirmModal';
import PlayerCard from './PlayerCard';
import CropModal from './CropModal';
import SmartLinkModal from './SmartLinkModal';
import ImportRosterModal from './ImportRosterModal';
import AdminHeader from '../AdminHeader';
import HighlightedText from '../HighlightedText';
import { formatRelativeTime } from '../../utils/dateUtils';

const AdminTeamDetail = ({ eventCode }) => {
  const { teamName } = useParams();
  const navigate = useNavigate();
  // const fileInputRef = useRef(null); // Unused
  const currentEventCode = eventCode || 'mjwc2025';
  const { isSuperAdmin } = useAuth();

  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [selectedPlayer, setSelectedPlayer] = useState(null); // Unused
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState(null); // { rows: [], playerData: [] }
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmedFilter, setConfirmedFilter] = useState('all'); // 'all' | 'confirmed' | 'unconfirmed'
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showImportRoster, setShowImportRoster] = useState(false);

  // Smart Keyboard Navigation
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept if user is already typing in an input or textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key === '/') {
        e.preventDefault(); // prevent typing the slash
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);


  // -- Linking/Import State (still needed in parent) --
  const [linkingData, setLinkingData] = useState(null); // { local: player, remote: person }
  const [importConfirmation, setImportConfirmation] = useState(null); // { person: remotePerson, customName: string }
  const [smartLinkData, setSmartLinkData] = useState(null); // { candidate: existingPlayer, remotePerson: sportradarPerson }
  // ----------------------

  // -- Crop Modal State --
  const [cropData, setCropData] = useState(null); // { player: player, imageUrl: url, mode: 'sync' | 'cropOnly' }
  // ----------------------

  // -- Link Modal Initial Query --
  const [linkPlayerQuery, setLinkPlayerQuery] = useState(''); // Player name for auto-populating search
  const [linkingPlayer, setLinkingPlayer] = useState(null); // Player being linked (when opened from Link button)
  // ----------------------


  // Normalize Name Helper
  const normalizeName = (name) => {
    if (!name) return '';
    // Remove punctuation, extra spaces, lowercase
    const clean = name.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
    // Sort tokens to handle "First Last" vs "Last First"
    return clean.split(' ').sort().join(' ');
  };

  const handleConnectPlayer = async (remotePerson) => {
    // Flow 1: Direct linking from player card Link button
    if (linkingPlayer) {
      if (linkingPlayer.person_id) {
        toast.error(`Player ${linkingPlayer.player_name} is already linked.`);
        return;
      }
      setLinkingData({ local: linkingPlayer, remote: remotePerson });
      return;
    }

    // Flow 2: Adding new player via Add Player button
    const remoteLatin = normalizeName(remotePerson.nameFullLatin);
    const remoteLocal = normalizeName(remotePerson.nameFullLocal);

    // A. Check Local Duplicate (Prevent adding same person twice to THIS team)
    const localMatch = players.find(p => {
      // 1. Absolute ID Match
      if (p.person_id && remotePerson.personId && p.person_id === remotePerson.personId) {
        return true;
      }

      const local = normalizeName(p.player_name);
      const nameMatch = local && (local === remoteLatin || local === remoteLocal);

      if (nameMatch) {
        // If they have DIFFERENT person_ids, they are DIFFERENT people (same name)
        if (p.person_id && remotePerson.personId && p.person_id !== remotePerson.personId) {
          return false;
        }
        // If we have DOB on both and they are DIFFERENT, they are DIFFERENT people
        if (p.dob && remotePerson.dob && p.dob !== remotePerson.dob) {
          return false;
        }
        return true; // Likely a duplicate or legacy link
      }
      return false;
    });

    if (localMatch) {
      if (localMatch.person_id) {
        toast.error(`Player ${localMatch.player_name} is already linked.`);
        return;
      }
      setLinkingData({ local: localMatch, remote: remotePerson });
      return;
    }

    // B. Smart Check 1: ID Match (Auto-Link / Self-Healing)
    if (remotePerson.personId) {
      const { data: idMatches } = await adminService.globalSearch(remotePerson.personId);
      if (idMatches && idMatches.length > 0) {
        const existing = idMatches[0];
        toast.success(`Found existing record for ${existing.player_name}. Using existing photo.`);

        setImportConfirmation({
          person: remotePerson,
          customName: remotePerson.nameFullLatin,
          smartLinkPhoto: existing.photo_url || existing.uploaded_photo_url
        });
        return;
      }
    }

    // C. Smart Check 2: Fuzzy Name Match (Manual Confirm)
    if (remotePerson.nameFullLatin) {
      const { data: nameMatches } = await adminService.globalSearch(remotePerson.nameFullLatin);

      // Client-side similarity check
      const bestMatch = nameMatches?.find(p => {
        const score = calculateSimilarity(p.player_name, remotePerson.nameFullLatin);
        return score > 0.6;
      });

      if (bestMatch && bestMatch.person_id !== remotePerson.personId) {
        setSmartLinkData({ candidate: bestMatch, remotePerson });
        return;
      }
    }

    // D. Fallback (Standard Import)
    setImportConfirmation({
      person: remotePerson,
      customName: remotePerson.nameFullLatin || remotePerson.nameFullLocal
    });
  };

  // Smart Link Handlers
  const handleConfirmSmartLink = async (candidate) => {
    if (!smartLinkData) return;
    const { remotePerson, isManual } = smartLinkData;

    // 1. Link the OLD record (Self-Healing)
    const { error: linkError } = await adminService.linkExistingPlayer(candidate.id, remotePerson.personId);
    if (linkError) {
      console.error('Failed to self-heal old record:', linkError);
    } else {
      toast.success(`Linked historical record for ${candidate.player_name}`);
    }

    // 2. Import NEW record
    setSmartLinkData(null);

    if (isManual) {
      await performManualAdd(remotePerson.nameFullLatin, candidate);
    } else {
      setImportConfirmation({
        person: remotePerson,
        customName: remotePerson.nameFullLatin,
        smartLinkPhoto: candidate.photo_url || candidate.uploaded_photo_url
      });
    }
  };

  const handleIgnoreSmartLink = () => {
    if (!smartLinkData) return;
    const { remotePerson, isManual } = smartLinkData;
    setSmartLinkData(null);

    if (isManual) {
      performManualAdd(remotePerson.nameFullLatin);
    } else {
      setImportConfirmation({
        person: remotePerson,
        customName: remotePerson.nameFullLatin || remotePerson.nameFullLocal
      });
    }
  };

  // Levenshtein Similarity Helper (Client-Side)
  const calculateSimilarity = (str1, str2) => {
    const s1 = normalizeName(str1);
    const s2 = normalizeName(str2);
    if (!s1 || !s2) return 0;

    const track = Array(s2.length + 1).fill(null).map(() =>
      Array(s1.length + 1).fill(null));
    for (let i = 0; i <= s1.length; i += 1) { track[0][i] = i; }
    for (let j = 0; j <= s2.length; j += 1) { track[j][0] = j; }
    for (let j = 1; j <= s2.length; j += 1) {
      for (let i = 1; i <= s1.length; i += 1) {
        const indicator = s1[i - 1] === s2[j - 1] ? 0 : 1;
        track[j][i] = Math.min(
          track[j][i - 1] + 1, // deletion
          track[j - 1][i] + 1, // insertion
          track[j - 1][i - 1] + indicator, // substitution
        );
      }
    }
    const distance = track[s2.length][s1.length];
    const maxLength = Math.max(s1.length, s2.length);
    return 1 - (distance / maxLength);
  };

  // Helper for actual manual insertion
  const performManualAdd = async (playerName, linkedCandidate = null) => {
    try {
      if (!eventData?.short_name) {
        toast.error('Event data not loaded');
        return;
      }
      const eventName = eventData.short_name;

      // Reuse the matched candidate's photo only if it actually has one.
      const linkedPhotoUrl = linkedCandidate
        ? (linkedCandidate.photo_url || linkedCandidate.uploaded_photo_url || null)
        : null;

      const payload = {
        team: team.name,
        player_name: playerName,
        event_name: eventName,
        // Keep upload_status in lockstep with photo_url: a linked-but-photoless
        // candidate must NOT count as complete (otherwise the progress bar shows
        // a player as done while the card renders "No Photo").
        upload_status: !!linkedPhotoUrl,
        photo_url: linkedPhotoUrl,
        person_id: linkedCandidate?.person_id || null, // Reuse Person ID if available
        dob: linkedCandidate?.dob || null,
        gender: linkedCandidate?.gender || null,
        nationality: linkedCandidate?.nationality || null,
        sportradar_photo_url: linkedCandidate?.sportradar_photo_url || null
      };

      const { error } = await supabase
        .from('event_uploads')
        .insert([payload]);

      if (error) throw error;

      toast.success(`Added ${playerName}${linkedCandidate ? ' (Linked)' : ''}`);
      fetchPlayers();
    } catch (error) {
      console.error('Manual Add Error:', error);
      toast.error('Failed to add player');
    }
  };

  // Handler for Manual Add Request (from Modal)
  const handleAddManualPlayer = async (playerName) => {
    // 1. Smart Check
    const { data: matches } = await adminService.globalSearch(playerName);

    // Client-side similarity check (consistent with Sportradar import)
    const bestMatch = matches?.find(p => {
      const score = calculateSimilarity(p.player_name, playerName);
      return score > 0.6;
    });

    if (bestMatch) {
      // Manual entry has no person ID. Mock one for the modal.
      const mockPerson = {
        nameFullLatin: playerName,
        personId: null,
        nationality: 'Manual Entry',
        images: []
      };
      setSmartLinkData({ candidate: bestMatch, remotePerson: mockPerson, isManual: true });
      return;
    }

    // 2. Direct Add
    await performManualAdd(playerName);
  };


  const confirmImport = async () => {
    if (!importConfirmation || !eventData?.short_name) return;
    const { person, customName, smartLinkPhoto } = importConfirmation;
    const eventName = eventData.short_name;

    let storedPhotoUrl = smartLinkPhoto || null;

    // If person has a Sportradar photo, store it in Supabase first
    if (person.images && person.images.length > 0) {
      const importToast = toast.loading('Storing photo...');
      try {
        const { storedUrl } = await adminService.storeSportradarPhoto(
          person.personId,
          customName || person.nameFullLatin || person.nameFullLocal,
          currentEventCode,
          team.name
        );
        if (storedUrl) {
          storedPhotoUrl = storedUrl;
          toast.dismiss(importToast);
        } else {
          toast.dismiss(importToast);
          // Continue import without stored photo
        }
      } catch (err) {
        console.error('Store photo error:', err);
        toast.dismiss(importToast);
        // Continue import without stored photo
      }
    }

    // Import the player (with stored photo URL if available)
    const { error } = await adminService.importSportradarPlayer(person, eventName, team.name, customName, storedPhotoUrl);
    if (error) {
      toast.error('Import failed');
    } else {
      toast.success(`Imported ${customName}`);
      fetchPlayers();
      setImportConfirmation(null);
    }
  };

  const confirmLink = async (useRemotePhoto) => {
    if (!linkingData || !eventData) return;

    const { local, remote } = linkingData;
    let photoUrlToUse = null;

    // If user wants to use Sportradar photo, download and store it in our storage
    if (useRemotePhoto && remote.images && remote.images.length > 0) {
      const linkToast = toast.loading('Storing Sportradar photo...');

      try {
        const { storedUrl, error } = await adminService.storeSportradarPhoto(
          remote.personId,  // Use personId - Edge Function will fetch the image
          local.player_name,
          currentEventCode,
          team.name
        );

        if (!storedUrl) {
          toast.error('Failed to store photo: ' + (error?.message || 'Unknown error'), { id: linkToast });
          // Continue linking without photo
        } else {
          photoUrlToUse = storedUrl;
          toast.dismiss(linkToast);
        }
      } catch (err) {
        console.error('Store photo error:', err);
        toast.error('Failed to store photo', { id: linkToast });
        // Continue linking without photo
      }
    }

    const { error } = await adminService.linkSportradarPlayer(local.id, remote, photoUrlToUse);
    if (error) {
      toast.error('Linking failed');
    } else {
      // Optimistic update - show linked status immediately
      const updates = { person_id: remote.personId };
      if (photoUrlToUse) {
        updates.photo_url = photoUrlToUse;
        updates.upload_status = true;
      }
      // Store original SR URL for reference
      if (remote.images && remote.images.length > 0) {
        updates.sportradar_photo_url = `${remote.images[0].url}?size=RAW&format=webp`;
      }
      updatePlayerLocally(local.id, updates);

      toast.success('Player linked successfully');
      setLinkingData(null);
      setLinkingPlayer(null); // Clear linking player state
    }
  };

  // First fetch the team name from the database to get the correct casing
  const [teamData, setTeamData] = useState(null);

  // Fetch event details from database to get correct short_name
  const [eventData, setEventData] = useState(null);

  // Fetch event info first (needed for all queries)
  useEffect(() => {
    const fetchEventInfo = async () => {
      const { data: event } = await supabase
        .from('events')
        .select('*')
        .eq('code', currentEventCode)
        .single();

      if (event) {
        setEventData(event);
      }
    };
    fetchEventInfo();
  }, [currentEventCode]);

  // Add this useEffect to fetch team info
  useEffect(() => {
    const fetchTeamInfo = async () => {
      if (!eventData?.short_name) return; // Wait for event data

      const { data } = await supabase
        .from('event_uploads')
        .select('team')
        .eq('event_name', eventData.short_name)
        .ilike('team', teamName?.replace(/-/g, ' ') || '')
        .limit(1);

      if (data && data.length > 0) {
        setTeamData({
          name: data[0].team
        });
      }
    };

    if (teamName && eventData) fetchTeamInfo();
  }, [teamName, eventData]);

  const team = teamData || {
    name: teamName?.replace(/-/g, ' ')
  };



  const fetchPlayers = useCallback(async () => {
    if (!eventData?.short_name) return; // Wait for event data

    setLoading(true);
    try {
      const eventName = eventData.short_name;

      const { data, error } = await supabase
        .from('event_uploads')
        .select('*')
        .eq('team', teamData?.name || teamName.replace(/-/g, ' '))
        .eq('event_name', eventName)  // Use database short_name
        .order('player_name', { ascending: true });

      if (error) throw error;
      setPlayers(data || []);
    } catch (error) {
      console.error('Error fetching players:', error);
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [teamData, teamName, eventData]);

  useEffect(() => {
    // Only fetch players if teamData is loaded
    if (teamData) {
      fetchPlayers();
    }
  }, [teamName, currentEventCode, teamData, fetchPlayers]); // Add teamData to dependencies

  // Filter players based on search + confirmed status
  const filteredPlayers = players.filter(player => {
    const matchesSearch = player.player_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesConfirmed =
      confirmedFilter === 'all' ||
      (confirmedFilter === 'confirmed' ? !!player.confirmed : !player.confirmed);
    return matchesSearch && matchesConfirmed;
  });

  const confirmedCount = players.filter(p => p.confirmed).length;

  // Compute duplicate name detection across the filtered roster
  const duplicatePlayerIds = (() => {
    const ids = new Set();
    const norm = (name) => (name || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z\s]/g, '').trim();
    for (let i = 0; i < filteredPlayers.length; i++) {
      for (let j = i + 1; j < filteredPlayers.length; j++) {
        const normA = norm(filteredPlayers[i].player_name);
        const normB = norm(filteredPlayers[j].player_name);
        // Only flag as duplicates if the normalized names are exactly equal
        // This avoids substring matching false positives like "John" vs "John Smith"
        if (normA && normB && normA === normB) {
          ids.add(filteredPlayers[i].id);
          ids.add(filteredPlayers[j].id);
        }
      }
    }
    return ids;
  })();

  // ========== Local state update helpers ==========
  // Every write path below updates local state directly instead of refetching.
  // fetchPlayers() flips `loading`, which unmounts the grid and snaps scroll back
  // to the top — unusable when working down a long roster. The DB write has already
  // succeeded by the time these run, and none of these fields are server-derived,
  // so local state is the source of truth until the next real load.

  // For writes that also set updated_at server-side.
  const updatePlayerLocally = (playerId, changes) => {
    setPlayers(prev => prev.map(p =>
      p.id === playerId ? { ...p, ...changes, updated_at: new Date().toISOString() } : p
    ));
  };

  // For writes that DON'T touch updated_at (e.g. sportradar_photo_url refreshes),
  // so the "last updated" pill doesn't drift ahead of the stored value.
  const patchPlayerLocally = (playerId, changes) => {
    setPlayers(prev => prev.map(p => (p.id === playerId ? { ...p, ...changes } : p)));
  };

  const removePlayerLocally = (playerId) => {
    setPlayers(prev => prev.filter(p => p.id !== playerId));
  };

  // ========== Confirmed flag (super_admin only) ==========
  // DB-enforced: a trigger rejects confirmed changes from non-super_admins,
  // so this update will error (and we revert) if a non-super somehow calls it.
  const setConfirmedFor = async (playerIds, value) => {
    if (!playerIds.length) return false;
    // Optimistic update
    setPlayers(prev => prev.map(p => playerIds.includes(p.id) ? { ...p, confirmed: value } : p));

    const { error } = await supabase
      .from('event_uploads')
      .update({ confirmed: value })
      .in('id', playerIds);

    if (error) {
      // Revert on failure
      setPlayers(prev => prev.map(p => playerIds.includes(p.id) ? { ...p, confirmed: !value } : p));
      toast.error(error.message || 'Failed to update confirmation');
      return false;
    }
    return true;
  };

  const handleToggleConfirmed = async (player) => {
    const next = !player.confirmed;
    const ok = await setConfirmedFor([player.id], next);
    if (ok) toast.success(next ? 'Player confirmed' : 'Confirmation removed');
  };


  // ================================================

  // Download single photo
  const downloadPhoto = async (player) => {
    try {
      const response = await fetch(player.photo_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${player.player_name}.jpg`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${player.player_name}'s photo`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download photo');
    }
  };

  // Download all photos as ZIP. When confirmedOnly is true, only confirmed players.
  const downloadAllPhotos = async (confirmedOnly = false) => {
    setIsDownloading(true);
    const zip = new JSZip();
    const folder = zip.folder(team.name);

    try {
      // Include players with photos (all photos are now in Supabase storage)
      const playersWithPhotos = players.filter(p => p.photo_url && (!confirmedOnly || p.confirmed));

      if (playersWithPhotos.length === 0) {
        toast.error(confirmedOnly ? 'No confirmed players with photos' : 'No photos to download');
        setIsDownloading(false);
        return;
      }

      // Download each photo and add to zip
      let successCount = 0;
      const promises = playersWithPhotos.map(async (player) => {
        try {
          const response = await fetch(player.photo_url);
          const blob = await response.blob();
          folder.file(`${player.player_name}.jpg`, blob);
          successCount++;
        } catch (error) {
          console.error(`Failed to download photo for ${player.player_name}:`, error);
        }
      });

      await Promise.all(promises);

      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${team.name}_${confirmedOnly ? 'confirmed_' : ''}photos_${new Date().toISOString().split('T')[0]}.zip`);

      toast.success(`Downloaded ${successCount} of ${playersWithPhotos.length} photos!`);
    } catch (error) {
      console.error('Bulk download error:', error);
      toast.error('Failed to create zip file');
    } finally {
      setIsDownloading(false);
    }
  };

  // Step 1: Preview accreditation export (CSV + Photos) for this team before zipping.
  // When confirmedOnly is true, restrict to confirmed players.
  const previewAccreditation = (confirmedOnly = false) => {
    const source = confirmedOnly ? players.filter(p => p.confirmed) : players;
    if (!source || source.length === 0) {
      toast.error(confirmedOnly ? 'No confirmed players for this team' : 'No players found for this team');
      return;
    }

    const stripAccents = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const csvRows = [['Group', 'Full Name', 'First Name', 'Last Name', 'Email', 'Country', 'Photos']];
    source.forEach(player => {
      const rawName = player.player_name || '';
      const normalizedName = stripAccents(rawName);
      const email = `${normalizedName.toLowerCase().replace(/[^a-z0-9]/g, '')}@example.com`;
      const photoFilename = player.photo_url ? `${normalizedName}.jpg` : '';
      csvRows.push([
        'Participating Team',
        normalizedName,
        stripAccents(player.first_name),
        stripAccents(player.last_name),
        email,
        player.team,
        photoFilename
      ]);
    });

    setCsvPreviewData({ rows: csvRows, playerData: source, confirmedOnly });
  };

  // Step 2: Confirmed accreditation export — builds ZIP (Photos/ folder + CSV) and downloads
  const confirmExportAccreditation = async () => {
    if (!csvPreviewData) return;
    const { playerData, confirmedOnly } = csvPreviewData;

    setIsExporting(true);
    const toastId = toast.loading('Building ZIP...');

    try {
      const zip = new JSZip();
      const photosFolder = zip.folder('Photos');

      const stripAccents = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const csvRows = [['Group', 'Full Name', 'First Name', 'Last Name', 'Email', 'Country', 'Photos']];

      playerData.forEach((player) => {
        const rawName = player.player_name || '';
        const normalizedName = stripAccents(rawName);
        const email = `${normalizedName.toLowerCase().replace(/[^a-z0-9]/g, '')}@example.com`;
        const photoFilename = player.photo_url ? `${normalizedName}.jpg` : '';

        csvRows.push([
          'Participating Team',
          `"${normalizedName}"`,
          `"${stripAccents(player.first_name)}"`,
          `"${stripAccents(player.last_name)}"`,
          email,
          `"${player.team}"`,
          `"${photoFilename}"`
        ]);
      });

      // Fetch photos in parallel without affecting CSV row order
      let successPhotoCount = 0;
      const promises = playerData.map(async (player) => {
        if (player.photo_url) {
          try {
            const response = await fetch(player.photo_url);
            const blob = await response.blob();
            const rawName = player.player_name || '';
            const normalizedName = rawName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const photoFilename = `${normalizedName}.jpg`;
            photosFolder.file(photoFilename, blob);
            successPhotoCount++;
          } catch (err) {
            console.error(`Failed to download photo for ${player.player_name}:`, err);
          }
        }
      });

      await Promise.all(promises);

      const BOM = '\uFEFF';
      const csvContent = BOM + csvRows.map(row => row.join(',')).join('\n');
      zip.file(`${team.name}_${confirmedOnly ? 'confirmed_' : ''}accreditation.csv`, csvContent);

      toast.loading(`Zipping ${successPhotoCount} photos...`, { id: toastId });
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${team.name}_${confirmedOnly ? 'confirmed_' : ''}accreditation_${new Date().toISOString().split('T')[0]}.zip`);

      toast.success('Accreditation data exported!', { id: toastId });
      setCsvPreviewData(null);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export accreditation data', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  // Delete photo
  const deletePhoto = async (player) => {
    if (!window.confirm(`Are you sure you want to delete ${player.player_name}'s photo?`)) {
      return;
    }

    try {
      // Parse the photo URL to get the file path
      // Parse the photo URL to get the file path
      const urlParts = player.photo_url.split('/');
      const fileName = `${currentEventCode}/${team.name}/${urlParts[urlParts.length - 1]}`;

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('player-photos')
        .remove([fileName]);

      if (storageError) throw storageError;

      // Update database
      const { error: dbError } = await supabase
        .from('event_uploads')
        .update({
          upload_status: false,
          photo_url: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (dbError) throw dbError;

      updatePlayerLocally(player.id, { upload_status: false, photo_url: null });
      toast.success('Photo deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete photo');
    }
  };

  // Delete Entire Player
  const handleDeletePlayer = async (player) => {
    if (!window.confirm(
      `Remove ${player.player_name} from ${team.name}'s roster?\n\n` +
      `This deletes their roster entry for this event and any photos uploaded here.\n\n` +
      `This cannot be undone.`
    )) {
      return;
    }

    try {
      const { error } = await adminService.deletePlayer(player, currentEventCode);
      if (error) throw error;

      removePlayerLocally(player.id);
      toast.success(`Removed ${player.player_name} from the roster`);
    } catch (error) {
      console.error('Delete player error:', error);
      toast.error('Failed to delete player');
    }
  };

  // Replace photo
  const handleReplacePhoto = async (event, player) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Delete old photo first if it exists
      if (player.photo_url) {
        const urlParts = player.photo_url.split('/');
        const oldFileName = `${currentEventCode}/${team.name}/${urlParts[urlParts.length - 1]}`;
        await supabase.storage.from('player-photos').remove([oldFileName]);
      }

      // Upload new photo
      const fileName = `${currentEventCode}/${team.name}/${player.player_name.replace(/\s+/g, '_')}_${Date.now()}.jpg`;
      const { error: storageError } = await supabase.storage
        .from('player-photos')
        .upload(fileName, file, {
          contentType: 'image/jpeg',
          upsert: true
        });

      if (storageError) throw storageError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('player-photos')
        .getPublicUrl(fileName);

      // Update database - also save to uploaded_photo_url as backup
      const { error: dbError } = await supabase
        .from('event_uploads')
        .update({
          upload_status: true,
          photo_url: publicUrl,
          uploaded_photo_url: publicUrl,  // Backup user upload for restore
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (dbError) throw dbError;

      updatePlayerLocally(player.id, { upload_status: true, photo_url: publicUrl, uploaded_photo_url: publicUrl });
      toast.success(`Photo updated for ${player.player_name}`);
    } catch (error) {
      console.error('Replace error:', error);
      toast.error('Failed to replace photo');
    }
  };

  // addNewPlayer is now handled by AddPlayerModal component

  // Handle Edit Player Name
  const handleEditName = async (player, newName) => {
    try {
      const { error } = await supabase
        .from('event_uploads')
        .update({
          player_name: newName,
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (error) throw error;

      updatePlayerLocally(player.id, { player_name: newName });
      toast.success(`Renamed to "${newName}"`);
    } catch (error) {
      console.error('Edit name error:', error);
      toast.error('Failed to update name');
    }
  };

  // Handle Edit Player Details (display name + manual First/Last name)
  const handleEditPlayerDetails = async (player, { player_name, first_name, last_name }) => {
    const trimmedName = (player_name ?? player.player_name ?? '').trim();
    if (!trimmedName) {
      toast.error('Name cannot be empty');
      return;
    }
    const nextFirst = (first_name ?? '').trim() || null;
    const nextLast = (last_name ?? '').trim() || null;

    // No-op if nothing actually changed
    if (
      trimmedName === player.player_name &&
      nextFirst === (player.first_name || null) &&
      nextLast === (player.last_name || null)
    ) {
      return;
    }

    try {
      const { error } = await supabase
        .from('event_uploads')
        .update({
          player_name: trimmedName,
          first_name: nextFirst,
          last_name: nextLast,
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (error) throw error;

      updatePlayerLocally(player.id, { player_name: trimmedName, first_name: nextFirst, last_name: nextLast });
      toast.success('Player details updated');
    } catch (error) {
      console.error('Edit details error:', error);
      toast.error('Failed to update player details');
    }
  };

  // Handle Restore Uploaded Photo (undo Sportradar photo override)
  const handleRestorePhoto = async (player) => {
    if (!player.uploaded_photo_url) {
      toast.error('No backup photo available to restore');
      return;
    }

    if (!window.confirm('Restore the original uploaded photo? This will replace the current photo.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('event_uploads')
        .update({
          photo_url: player.uploaded_photo_url,
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (error) throw error;

      updatePlayerLocally(player.id, { photo_url: player.uploaded_photo_url });
      toast.success('Uploaded photo restored!');
    } catch (error) {
      console.error('Restore photo error:', error);
      toast.error('Failed to restore photo');
    }
  };

  // Handle Restore Sportradar Photo (re-download from Sportradar and store in Supabase)
  const handleRestoreSportradarPhoto = async (player) => {
    if (!player.person_id) {
      toast.error('Player is not linked to Sportradar');
      return;
    }

    if (!window.confirm('Restore photo from Sportradar? This will download and replace the current photo.')) {
      return;
    }

    const restoreToast = toast.loading('Downloading Sportradar photo...');

    try {
      const { storedUrl, error } = await adminService.storeSportradarPhoto(
        player.person_id,
        player.player_name,
        currentEventCode,
        team.name
      );

      if (!storedUrl) {
        toast.error('Failed to download photo: ' + (error?.message || 'Unknown error'), { id: restoreToast });
        return;
      }

      // Update database with new stored URL
      const { error: dbError } = await supabase
        .from('event_uploads')
        .update({
          photo_url: storedUrl,
          upload_status: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (dbError) throw dbError;

      updatePlayerLocally(player.id, { photo_url: storedUrl, upload_status: true });
      toast.success('Sportradar photo restored!', { id: restoreToast });
    } catch (error) {
      console.error('Restore SR photo error:', error);
      toast.error('Failed to restore Sportradar photo', { id: restoreToast });
    }
  };

  // Import a submitted team list (LongList or Accreditation List). Parsing lives in
  // utils/rosterParser; this writes only the rows ticked on the review step.
  //
  // Photos are pulled afterwards for the people the reviewer confirmed a Sportradar link
  // for. That is a read from Sportradar into our own storage — nothing is sent back.
  const handleRosterImport = async (people) => {
    if (!eventData?.short_name) {
      toast.error('Event data not loaded');
      return;
    }

    const { data: created, error } = await adminService.importPlayers(people, eventData.short_name, team.name);
    if (error) {
      toast.error('Import failed: ' + error.message);
      return;
    }

    setShowImportRoster(false);
    toast.success('Imported ' + people.length + (people.length === 1 ? ' person' : ' people'));

    // The image URL travelled with the payload, so pair each created row back up with it
    // rather than asking Sportradar to resolve the person a second time.
    const photoUrlByPerson = new Map(
      people.filter(p => p.person_id && p.sportradar_photo_url)
        .map(p => [p.person_id, p.sportradar_photo_url])
    );

    const linked = (created || []).filter(p => p.person_id && photoUrlByPerson.has(p.person_id));
    if (!linked.length) {
      fetchPlayers();
      return;
    }

    setIsProcessingBg(true);
    setProcessingTitle('Fetching Sportradar photos...');
    setProcessingProgress({ current: 0, total: linked.length });

    let done = 0;
    let withPhoto = 0;
    const queue = [...linked];

    // A few at a time: one at a time is slow for a full squad, and all at once is
    // needlessly hard on the photo endpoint.
    const worker = async () => {
      for (; ;) {
        const player = queue.shift();
        if (!player) return;
        try {
          const { url } = await adminService.copySportradarPhoto({
            imageUrl: photoUrlByPerson.get(player.person_id),
            eventCode: currentEventCode,
            teamName: team.name,
            playerName: player.player_name,
          });
          if (url) {
            const { error: dbError } = await adminService.setPlayerPhoto(player.id, url);
            if (!dbError) withPhoto++;
          }
        } catch (photoError) {
          // One unreachable photo should not stop the rest of the squad.
          console.error('Roster photo fetch failed:', photoError);
        }
        setProcessingProgress({ current: ++done, total: linked.length });
      }
    };

    await Promise.all(Array.from({ length: Math.min(3, linked.length) }, worker));

    setIsProcessingBg(false);
    if (withPhoto) {
      toast.success(`Downloaded ${withPhoto} of ${linked.length} Sportradar photos`);
    } else {
      toast('No Sportradar photos were available for these players', { icon: 'ℹ️' });
    }
    fetchPlayers();
  };

  // Open lightbox
  const openLightbox = (player) => {
    setLightboxImage(player);
    setShowLightbox(true);
  };

  // --- Background Removal Features ---
  const [selectedPlayers, setSelectedPlayers] = useState(new Set());
  const [isProcessingBg, setIsProcessingBg] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
  const [processingTitle, setProcessingTitle] = useState('Processing...');

  // REMBG API key is now stored server-side in Edge Function secrets

  // Toggle selection
  const toggleSelection = (playerId) => {
    const newSelected = new Set(selectedPlayers);
    if (newSelected.has(playerId)) {
      newSelected.delete(playerId);
    } else {
      newSelected.add(playerId);
    }
    setSelectedPlayers(newSelected);
  };

  // Select All
  const toggleSelectAll = () => {
    if (selectedPlayers.size === filteredPlayers.length) {
      setSelectedPlayers(new Set());
    } else {
      setSelectedPlayers(new Set(filteredPlayers.map(p => p.id)));
    }
  };

  // Bulk confirm/unconfirm the currently selected players (super_admin only).
  const handleBulkConfirm = async (value) => {
    const ids = Array.from(selectedPlayers);
    if (!ids.length) return;
    const ok = await setConfirmedFor(ids, value);
    if (ok) {
      toast.success(`${ids.length} player${ids.length > 1 ? 's' : ''} ${value ? 'confirmed' : 'unconfirmed'}`);
      setSelectedPlayers(new Set());
    }
  };

  // Bulk delete the currently selected players (roster row + their stored photos).
  // Deletes run sequentially so one failure doesn't abort the rest; each player is
  // dropped from local state only after its own delete succeeds.
  const handleBulkDelete = async () => {
    const targets = Array.from(selectedPlayers)
      .map(id => players.find(p => p.id === id))
      .filter(Boolean);

    if (targets.length === 0) return;

    const label = `${targets.length} player${targets.length > 1 ? 's' : ''}`;
    if (!window.confirm(
      `Remove ${label} from ${team.name}'s roster?\n\n` +
      `This deletes their roster entry for this event and any photos uploaded here.\n\n` +
      `This cannot be undone.`
    )) {
      return;
    }

    setIsProcessingBg(true);
    setProcessingTitle('Removing Players...');
    setProcessingProgress({ current: 0, total: targets.length });

    const deletedIds = [];
    const failedNames = [];

    try {
      for (let i = 0; i < targets.length; i++) {
        const player = targets[i];
        setProcessingProgress({ current: i + 1, total: targets.length });

        try {
          const { error } = await adminService.deletePlayer(player, currentEventCode);
          if (error) throw error;
          deletedIds.push(player.id);
        } catch (err) {
          console.error(`Failed to delete ${player.player_name}:`, err);
          failedNames.push(player.player_name);
        }
      }

      // Local removal only, no refetch — see handleDeletePlayer for why.
      if (deletedIds.length > 0) {
        const deletedSet = new Set(deletedIds);
        setPlayers(prev => prev.filter(p => !deletedSet.has(p.id)));
      }
      setSelectedPlayers(new Set());

      if (failedNames.length === 0) {
        toast.success(`Removed ${deletedIds.length} player${deletedIds.length > 1 ? 's' : ''} from the roster`);
      } else {
        const shown = failedNames.slice(0, 3).join(', ');
        const more = failedNames.length > 3 ? ` +${failedNames.length - 3} more` : '';
        toast.error(`Removed ${deletedIds.length}, failed ${failedNames.length}: ${shown}${more}`);
      }
    } finally {
      setIsProcessingBg(false);
      setProcessingProgress({ current: 0, total: 0 });
    }
  };

  // Handle Background Removal
  const handleRemoveBackground = async () => {
    if (selectedPlayers.size === 0) return;
    if (!window.confirm(`Remove background for ${selectedPlayers.size} photos? This may take a moment.`)) return;

    setIsProcessingBg(true);
    setProcessingTitle('Removing Backgrounds...');
    setProcessingProgress({ current: 0, total: selectedPlayers.size });
    let successCount = 0;
    let failCount = 0;

    try {
      // Convert Set to Array
      const idsToProcess = Array.from(selectedPlayers);

      // Process sequentially to be safe (could do parallel but check rate limits)
      for (let i = 0; i < idsToProcess.length; i++) {
        const playerId = idsToProcess[i];
        setProcessingProgress({ current: i + 1, total: idsToProcess.length });

        const player = players.find(p => p.id === playerId);
        if (!player || !player.photo_url) continue;

        try {
          // 1. Backup if needed
          if (!player.original_photo_url) {
            const { error: backupError } = await supabase
              .from('event_uploads')
              .update({ original_photo_url: player.photo_url })
              .eq('id', playerId);
            if (backupError) throw backupError;
          }

          // 2. Call API
          // Note: using the *current* photo_url. If it was already processed, it will process again.
          const processedBlob = await adminService.removeBackground(player.photo_url);

          // 3. Upload Result
          const fileName = `${currentEventCode}/${team.name}/${player.player_name.replace(/\s+/g, '_')}_nobg_${Date.now()}.png`;
          const { error: uploadError } = await supabase.storage
            .from('player-photos')
            .upload(fileName, processedBlob, {
              contentType: 'image/png',
              upsert: true
            });

          if (uploadError) throw uploadError;

          // 4. Update Database
          const { data: { publicUrl } } = supabase.storage
            .from('player-photos')
            .getPublicUrl(fileName);

          const { error: updateError } = await supabase
            .from('event_uploads')
            .update({
              photo_url: publicUrl,
              nobg_photo_url: publicUrl,
              updated_at: new Date().toISOString()
            })
            .eq('id', playerId);

          if (updateError) throw updateError;

          updatePlayerLocally(playerId, {
            photo_url: publicUrl,
            nobg_photo_url: publicUrl,
            original_photo_url: player.original_photo_url || player.photo_url
          });
          successCount++;

        } catch (err) {
          console.error(`Failed for ${player.player_name}:`, err);
          failCount++;
        }
      }

      toast.success(`Processed: ${successCount} success, ${failCount} failed`);
      setSelectedPlayers(new Set());

    } catch (error) {
      console.error('Batch processing error:', error);
      toast.error('Batch processing failed');
    } finally {
      setIsProcessingBg(false);
      setProcessingProgress({ current: 0, total: 0 });
    }
  };

  // Remove background for a single player (from 3-dot menu)
  const handleRemoveBackgroundSingle = async (player) => {
    if (!player.photo_url) {
      toast.error('No photo to process');
      return;
    }

    setIsProcessingBg(true);
    setProcessingProgress({ current: 0, total: 1 });

    try {
      setProcessingProgress({ current: 1, total: 1 });

      // 1. Backup if needed
      if (!player.original_photo_url) {
        const { error: backupError } = await supabase
          .from('event_uploads')
          .update({ original_photo_url: player.photo_url })
          .eq('id', player.id);
        if (backupError) throw backupError;
      }

      // 2. Call API
      const processedBlob = await adminService.removeBackground(player.photo_url);

      // 3. Upload Result
      const fileName = `${currentEventCode}/${team.name}/${player.player_name.replace(/\s+/g, '_')}_nobg_${Date.now()}.png`;
      const { error: uploadError } = await supabase.storage
        .from('player-photos')
        .upload(fileName, processedBlob, {
          contentType: 'image/png',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // 4. Update Database
      const { data: { publicUrl } } = supabase.storage
        .from('player-photos')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('event_uploads')
        .update({
          photo_url: publicUrl,
          nobg_photo_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (updateError) throw updateError;

      updatePlayerLocally(player.id, {
        photo_url: publicUrl,
        nobg_photo_url: publicUrl,
        original_photo_url: player.original_photo_url || player.photo_url
      });
      toast.success(`Background removed for ${player.player_name}`);

    } catch (error) {
      console.error('Remove background error:', error);
      toast.error('Failed to remove background: ' + error.message);
    } finally {
      setIsProcessingBg(false);
      setProcessingProgress({ current: 0, total: 0 });
    }
  };

  // Batch Sync to Sportradar (for selected SR-linked players with photos)
  const handleBatchSyncToSportradar = async () => {
    // Filter to only SR-linked players with photos
    const eligiblePlayers = Array.from(selectedPlayers)
      .map(id => players.find(p => p.id === id))
      .filter(p => p && p.person_id && p.photo_url);

    if (eligiblePlayers.length === 0) {
      toast.error('No eligible players selected. Players must be linked to Sportradar and have a photo.');
      return;
    }

    if (!window.confirm(`Sync ${eligiblePlayers.length} photo(s) to Sportradar? This will update their Sportradar profiles.`)) {
      return;
    }

    setIsProcessingBg(true);
    setProcessingTitle('Syncing to Sportradar...');
    setProcessingProgress({ current: 0, total: eligiblePlayers.length });
    let successCount = 0;
    let failCount = 0;

    try {
      for (let i = 0; i < eligiblePlayers.length; i++) {
        const player = eligiblePlayers[i];
        setProcessingProgress({ current: i + 1, total: eligiblePlayers.length });

        try {
          // Call the sync Edge Function directly with current photo
          const { newImageUrl, error } = await adminService.updateSportradarPhoto(
            player.person_id,
            player.photo_url
          );

          if (error) throw error;

          // Update local database with new Sportradar image URL
          if (newImageUrl) {
            await supabase
              .from('event_uploads')
              .update({ sportradar_photo_url: `${newImageUrl}?size=RAW&format=webp` })
              .eq('id', player.id);
            patchPlayerLocally(player.id, { sportradar_photo_url: `${newImageUrl}?size=RAW&format=webp` });
          }

          successCount++;
        } catch (err) {
          console.error(`Failed to sync ${player.player_name}:`, err);
          failCount++;
        }
      }

      toast.success(`Synced: ${successCount} success, ${failCount} failed`);
      setSelectedPlayers(new Set());

    } catch (error) {
      console.error('Batch sync error:', error);
      toast.error('Batch sync failed');
    } finally {
      setIsProcessingBg(false);
      setProcessingProgress({ current: 0, total: 0 });
    }
  };

  const handleBatchSyncFromSportradar = async () => {
    // Filter to only SR-linked players (photo will be pulled FROM Sportradar)
    const eligiblePlayers = Array.from(selectedPlayers)
      .map(id => players.find(p => p.id === id))
      .filter(p => p && p.person_id);

    if (eligiblePlayers.length === 0) {
      toast.error('No eligible players selected. Players must be linked to Sportradar.');
      return;
    }

    if (!window.confirm(`Pull ${eligiblePlayers.length} photo(s) from Sportradar? This will replace their current photo with the Sportradar profile photo.`)) {
      return;
    }

    setIsProcessingBg(true);
    setProcessingTitle('Syncing from Sportradar...');
    setProcessingProgress({ current: 0, total: eligiblePlayers.length });
    let successCount = 0;
    let failCount = 0;

    try {
      for (let i = 0; i < eligiblePlayers.length; i++) {
        const player = eligiblePlayers[i];
        setProcessingProgress({ current: i + 1, total: eligiblePlayers.length });

        try {
          // Download the Sportradar photo into Supabase storage
          const { storedUrl, error } = await adminService.storeSportradarPhoto(
            player.person_id,
            player.player_name,
            currentEventCode,
            team.name
          );

          if (error) throw error;
          if (!storedUrl) throw new Error('No photo found in Sportradar');

          // Make the Sportradar photo the player's active photo,
          // backing up any existing uploaded photo before overwriting
          const updates = {
            photo_url: storedUrl,
            upload_status: true,
            updated_at: new Date().toISOString()
          };
          if (player.photo_url && player.photo_url !== storedUrl) {
            updates.uploaded_photo_url = player.photo_url;
          }

          const { error: updateError } = await supabase
            .from('event_uploads')
            .update(updates)
            .eq('id', player.id);

          if (updateError) throw updateError;

          updatePlayerLocally(player.id, updates);
          successCount++;
        } catch (err) {
          console.error(`Failed to sync ${player.player_name} from Sportradar:`, err);
          failCount++;
        }
      }

      toast.success(`Synced: ${successCount} success, ${failCount} failed`);
      setSelectedPlayers(new Set());

    } catch (error) {
      console.error('Batch sync-from error:', error);
      toast.error('Batch sync failed');
    } finally {
      setIsProcessingBg(false);
      setProcessingProgress({ current: 0, total: 0 });
    }
  };

  // Toggle between Original and No-BG
  const handleTogglePhoto = async (player) => {
    if (!player.original_photo_url || !player.nobg_photo_url) return;

    // Check which one is currently active
    const isCurrentlyNoBg = player.photo_url === player.nobg_photo_url;

    const targetUrl = isCurrentlyNoBg ? player.original_photo_url : player.nobg_photo_url;
    const actionLabel = isCurrentlyNoBg ? "Original" : "No-BG";

    try {
      const { error } = await supabase
        .from('event_uploads')
        .update({
          photo_url: targetUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (error) throw error;

      updatePlayerLocally(player.id, { photo_url: targetUrl });
      toast.success(`Switched to ${actionLabel}`);
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('Failed to toggle photo');
    }
  };

  // Restore original photo (pre-background-removal from original_photo_url)
  const handleRestoreOriginalPhoto = async (player) => {
    if (!player.original_photo_url) {
      toast.error('No original photo backup available');
      return;
    }

    if (!window.confirm('Restore the original photo (before background removal)? This will replace the current photo.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('event_uploads')
        .update({
          photo_url: player.original_photo_url,
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (error) throw error;

      updatePlayerLocally(player.id, { photo_url: player.original_photo_url });
      toast.success('Original photo restored!');
    } catch (error) {
      console.error('Restore original error:', error);
      toast.error('Failed to restore original photo');
    }
  };

  // ------------------------------

  // Open Link Modal (opens AddPlayerModal with player name pre-filled and tracks which player to link)
  const handleOpenLink = (player) => {
    setLinkPlayerQuery(player.player_name);
    setLinkingPlayer(player); // Track which player we're linking
    setShowAddPlayer(true);
  };

  // Open Crop Modal before syncing to Sportradar
  const handleSyncToSportradar = async (player) => {
    if (!player.person_id) {
      toast.error('Player is not linked to Sportradar');
      return;
    }
    if (!player.photo_url) {
      toast.error('No photo to sync');
      return;
    }

    // Open crop modal in sync mode
    setCropData({
      player: player,
      imageUrl: player.photo_url,
      mode: 'sync'
    });
  };

  // Open Crop Modal for crop-only (from 3-dot menu)
  const handleCropPhotoOnly = (player) => {
    if (!player.photo_url) {
      toast.error('No photo to crop');
      return;
    }

    // Open crop modal in crop-only mode
    setCropData({
      player: player,
      imageUrl: player.photo_url,
      mode: 'cropOnly'
    });
  };

  // Actual sync after cropping
  const handleCropConfirm = async (croppedDataUrl, croppedBlob) => {
    const player = cropData?.player;
    if (!player) return;

    setCropData(null); // Close modal

    const syncToast = toast.loading('Uploading cropped photo...');
    try {
      // 1. Upload cropped image to Supabase storage first
      const timestamp = Date.now();
      const fileName = `${currentEventCode}/${team.name}/${player.player_name.replace(/\s+/g, '_')}_cropped_${timestamp}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('player-photos')
        .upload(fileName, croppedBlob, {
          contentType: 'image/jpeg',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // 2. Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('player-photos')
        .getPublicUrl(fileName);

      toast.loading('Syncing to Sportradar...', { id: syncToast });

      // 3. Now sync to Sportradar using the public URL
      const { newImageUrl, error } = await adminService.updateSportradarPhoto(
        player.person_id,
        publicUrl  // Use the uploaded public URL
      );
      if (error) throw error;

      // 4. Update local database with new Sportradar image URL
      if (newImageUrl) {
        const { error: updateError } = await supabase
          .from('event_uploads')
          .update({ sportradar_photo_url: `${newImageUrl}?size=RAW&format=webp` })
          .eq('id', player.id);

        if (updateError) {
          console.error('Failed to update sportradar_photo_url:', updateError);
        }
      }

      // Show the new sportradar photo URL immediately. Only sportradar_photo_url
      // was written, so patch without bumping updated_at.
      if (newImageUrl) {
        patchPlayerLocally(player.id, { sportradar_photo_url: `${newImageUrl}?size=RAW&format=webp` });
      }

      toast.success('Photo synced to Sportradar!', { id: syncToast });
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Failed to sync photo: ' + error.message, { id: syncToast });
    }
  };

  // Handle crop confirm for crop-only mode (just save, no SR sync)
  const handleCropConfirmOnly = async (croppedDataUrl, croppedBlob) => {
    const player = cropData?.player;
    if (!player) return;

    setCropData(null); // Close modal

    const cropToast = toast.loading('Saving cropped photo...');
    try {
      // 1. Upload cropped image to Supabase storage
      const timestamp = Date.now();
      const fileName = `${currentEventCode}/${team.name}/${player.player_name.replace(/\s+/g, '_')}_cropped_${timestamp}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('player-photos')
        .upload(fileName, croppedBlob, {
          contentType: 'image/jpeg',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // 2. Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('player-photos')
        .getPublicUrl(fileName);

      // 3. Backup original photo if not already backed up
      const updateData = {
        photo_url: publicUrl,
        updated_at: new Date().toISOString()
      };

      if (!player.original_photo_url) {
        updateData.original_photo_url = player.photo_url;
      }

      // 4. Update database with new cropped photo
      const { error: updateError } = await supabase
        .from('event_uploads')
        .update(updateData)
        .eq('id', player.id);

      if (updateError) throw updateError;

      updatePlayerLocally(player.id, {
        photo_url: publicUrl,
        original_photo_url: player.original_photo_url || player.photo_url
      });
      toast.success('Photo cropped and saved!', { id: cropToast });
    } catch (error) {
      console.error('Crop save error:', error);
      toast.error('Failed to save cropped photo: ' + error.message, { id: cropToast });
    }
  };

  // View Sportradar Photo (fetch fresh URL)
  const handleViewSportradarPhoto = async (player) => {
    if (!player.person_id) {
      toast.error('Player is not linked to Sportradar');
      return;
    }

    const viewToast = toast.loading('Fetching Sportradar photo...');
    try {
      const { imageUrl, error } = await adminService.getSportradarPhoto(player.person_id);
      if (error) throw error;

      if (!imageUrl) {
        toast.error('No photo found in Sportradar', { id: viewToast });
        return;
      }

      // Update local database with fetched URL
      const { error: updateError } = await supabase
        .from('event_uploads')
        .update({ sportradar_photo_url: `${imageUrl}?size=RAW&format=webp` })
        .eq('id', player.id);

      if (updateError) {
        console.error('Failed to update sportradar_photo_url:', updateError);
      } else {
        patchPlayerLocally(player.id, { sportradar_photo_url: `${imageUrl}?size=RAW&format=webp` });
      }

      toast.dismiss(viewToast);
      // Open with RAW size for full quality viewing
      window.open(`${imageUrl}?size=RAW&format=webp`, '_blank');
    } catch (error) {
      console.error('View SR Photo error:', error);
      toast.error('Failed to fetch photo: ' + error.message, { id: viewToast });
    }
  };

  // PlayerCard is now imported from ./PlayerCard.js


  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-center" />

      {/* Progress Overlay */}
      <ProgressOverlay
        isProcessing={isProcessingBg}
        current={processingProgress.current}
        total={processingProgress.total}
        title={processingTitle}
      />



      {/* Header */}
      <AdminHeader currentPage="team" />

      {/* Team Info Section */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6 md:py-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="flex-1">
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-gray-400 text-[10px] font-black uppercase tracking-widest mb-4">
                <button
                  onClick={() => navigate('/admin/events')}
                  className="hover:text-mjwc-primary transition-colors flex items-center gap-1"
                >
                  Events
                </button>
                <svg className="w-2.5 h-2.5 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                </svg>
                <button
                  onClick={() => navigate(`/admin/${currentEventCode}`)}
                  className="hover:text-mjwc-primary transition-colors"
                >
                  {eventData?.short_name || currentEventCode}
                </button>
                <svg className="w-2.5 h-2.5 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-mjwc-primary">
                  {team.name}
                </span>
              </div>

              {/* Team Title with Flag */}
              <div className="flex items-center gap-4 mb-6">
                <div className="p-1 bg-gray-50 rounded-2xl shadow-inner border border-gray-100">
                  <FlagDisplay teamName={team.name} size="lg" className="rounded-xl" />
                </div>
                <div>
                  <h1 className="text-4xl font-black text-gray-900 tracking-tight leading-none uppercase italic">
                    {team.name}
                  </h1>
                  <p className="text-gray-500 font-medium mt-1">
                    Team Roster & Photo Management
                  </p>
                </div>
              </div>

              {/* Progress UI */}
              <div className="max-w-md bg-gray-50 rounded-2xl p-4 border border-gray-100 shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">Upload Progress</span>
                  <span className="text-xs font-black text-mjwc-primary">
                    {players.filter(p => p.upload_status && p.photo_url).length} / {players.length}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-mjwc-primary to-indigo-600 transition-all duration-1000"
                    style={{ width: `${players.length > 0 ? (players.filter(p => p.upload_status && p.photo_url).length / players.length) * 100 : 0}%` }}
                  />
                </div>
                {/* Confirmed count — accreditation-ready players */}
                <div className="flex justify-between items-center mt-3">
                  <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">Confirmed</span>
                  <span className="text-xs font-black text-green-600">
                    {confirmedCount} / {players.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Team Actions */}
            <div className="flex items-center gap-3">
              {eventData?.id && (
                <TeamSwitcher
                  eventId={eventData.id}
                  eventCode={currentEventCode}
                  currentTeamName={team.name}
                />
              )}
              {selectedPlayers.size > 0 && (
                <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4">
                  {/* Confirm / Unconfirm (super_admin only) */}
                  {isSuperAdmin && (
                    <>
                      <button
                        onClick={() => handleBulkConfirm(true)}
                        className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest shadow-lg hover:shadow-green-500/20 hover:-translate-y-0.5 transition-all"
                      >
                        {`Confirm (${selectedPlayers.size})`}
                      </button>
                      <button
                        onClick={() => handleBulkConfirm(false)}
                        className="bg-white text-green-700 border border-green-600 px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest shadow-sm hover:bg-green-50 transition-all"
                      >
                        Unconfirm
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleRemoveBackground}
                    disabled={isProcessingBg}
                    className="bg-mjwc-primary text-white px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest shadow-lg hover:shadow-indigo-500/20 hover:-translate-y-0.5 transition-all disabled:opacity-50"
                  >
                    {isProcessingBg ? '...' : `Clean BG (${selectedPlayers.size})`}
                  </button>
                  {/* Batch Sync TO SR (push local photo up to Sportradar) */}
                  {(() => {
                    const eligibleCount = Array.from(selectedPlayers)
                      .map(id => players.find(p => p.id === id))
                      .filter(p => p && p.person_id && p.photo_url).length;
                    return eligibleCount > 0 ? (
                      <button
                        onClick={handleBatchSyncToSportradar}
                        disabled={isProcessingBg}
                        className="bg-mjwc-secondary text-white px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest shadow-lg hover:shadow-cyan-500/20 hover:-translate-y-0.5 transition-all disabled:opacity-50"
                      >
                        {isProcessingBg ? '...' : `Sync to SR (${eligibleCount})`}
                      </button>
                    ) : null;
                  })()}
                  {/* Batch Sync FROM SR (pull Sportradar photo down into the app) */}
                  {(() => {
                    const eligibleCount = Array.from(selectedPlayers)
                      .map(id => players.find(p => p.id === id))
                      .filter(p => p && p.person_id).length;
                    return eligibleCount > 0 ? (
                      <button
                        onClick={handleBatchSyncFromSportradar}
                        disabled={isProcessingBg}
                        className="bg-mjwc-secondary text-white px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest shadow-lg hover:shadow-cyan-500/20 hover:-translate-y-0.5 transition-all disabled:opacity-50"
                      >
                        {isProcessingBg ? '...' : `Sync from SR (${eligibleCount})`}
                      </button>
                    ) : null;
                  })()}
                  {/* Destructive action last, separated from the rest */}
                  <button
                    onClick={handleBulkDelete}
                    disabled={isProcessingBg}
                    className="bg-red-600 text-white px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-widest shadow-lg hover:shadow-red-500/20 hover:-translate-y-0.5 transition-all disabled:opacity-50 ml-1"
                    title="Removes these players from this event's roster"
                  >
                    {isProcessingBg ? '...' : `Remove from Roster (${selectedPlayers.size})`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 shadow-sm sticky top-[57px] z-30 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center mb-2 gap-2">
            <input
              type="checkbox"
              onChange={toggleSelectAll}
              checked={filteredPlayers.length > 0 && selectedPlayers.size === filteredPlayers.length}
              className="w-4 h-4 rounded border-gray-300 text-mjwc-primary focus:ring-mjwc-primary"
            />
            <span className="text-sm text-gray-600">Select All</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search players... (Press '/')"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mjwc-primary"
              />
              <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Confirmed filter */}
            <select
              value={confirmedFilter}
              onChange={(e) => setConfirmedFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-mjwc-primary sm:min-w-[160px]"
              title="Filter by confirmed status"
            >
              <option value="all">All players</option>
              <option value="confirmed">Confirmed only</option>
              <option value="unconfirmed">Not confirmed</option>
            </select>

            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                title="Toggle view"
              >
                {viewMode === 'grid' ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                )}
              </button>

              <button
                onClick={() => setShowImportRoster(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                title="Import the team's LongList or Accreditation List"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <span className="hidden sm:inline">Import list</span>
              </button>

              <button
                onClick={() => setShowAddPlayer(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="hidden sm:inline">Add Player</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                  disabled={isDownloading || isExporting || players.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {(isDownloading || isExporting) ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="hidden sm:inline">Working...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span className="hidden sm:inline">Export</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </>
                  )}
                </button>

                {showExportDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-50 border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => {
                        setShowExportDropdown(false);
                        downloadAllPhotos();
                      }}
                      disabled={players.filter(p => p.photo_url).length === 0}
                      className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Photos only (ZIP)
                    </button>
                    <button
                      onClick={() => {
                        setShowExportDropdown(false);
                        previewAccreditation();
                      }}
                      className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Accreditation (CSV + Photos)
                    </button>

                    {/* Confirmed-only variants */}
                    <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50 border-t border-gray-100">
                      Confirmed only
                    </div>
                    <button
                      onClick={() => {
                        setShowExportDropdown(false);
                        downloadAllPhotos(true);
                      }}
                      disabled={players.filter(p => p.photo_url && p.confirmed).length === 0}
                      className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Confirmed Photos only (ZIP)
                    </button>
                    <button
                      onClick={() => {
                        setShowExportDropdown(false);
                        previewAccreditation(true);
                      }}
                      disabled={confirmedCount === 0}
                      className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Confirmed Accreditation (CSV + Photos)
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mjwc-primary"></div>
          </div>
        ) : filteredPlayers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No players found
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredPlayers.map((player) => (
              <PlayerCard
                key={player.id}
                player={player}
                isDuplicate={duplicatePlayerIds.has(player.id)}
                isSelected={selectedPlayers.has(player.id)}
                isSuperAdmin={isSuperAdmin}
                onToggleConfirmed={handleToggleConfirmed}
                onToggleSelection={toggleSelection}
                onTogglePhoto={handleTogglePhoto}
                onOpenLightbox={openLightbox}
                onDownload={downloadPhoto}
                onReplacePhoto={handleReplacePhoto}
                onDeletePhoto={deletePhoto}
                onDeletePlayer={handleDeletePlayer}
                onOpenLink={handleOpenLink}
                onSyncToSportradar={handleSyncToSportradar}
                onViewSportradarPhoto={handleViewSportradarPhoto}
                onEditName={handleEditName}
                onEditPlayerDetails={handleEditPlayerDetails}
                onRestorePhoto={handleRestorePhoto}
                onRestoreSportradarPhoto={handleRestoreSportradarPhoto}
                onRemoveBackground={handleRemoveBackgroundSingle}
                onCropPhoto={handleCropPhotoOnly}
                onRestoreOriginalPhoto={handleRestoreOriginalPhoto}
                searchQuery={searchQuery}
              />
            ))}
          </div>
        ) : (
          // List view
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-mjwc-primary focus:ring-mjwc-primary"
                      checked={filteredPlayers.length > 0 && selectedPlayers.size === filteredPlayers.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPlayers.map((player) => (
                  <tr key={player.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-mjwc-primary focus:ring-mjwc-primary"
                        checked={selectedPlayers.has(player.id)}
                        onChange={() => toggleSelection(player.id)}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <HighlightedText text={player.player_name} highlight={searchQuery} />
                        {player.confirmed && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700"
                            title="Confirmed for the final list"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                            Confirmed
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {player.upload_status ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Uploaded
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-medium">
                      {player.updated_at ? formatRelativeTime(player.updated_at) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleToggleConfirmed(player)}
                            className={`text-xs border px-2 py-1 rounded transition-colors ${player.confirmed
                              ? 'text-green-700 border-green-300 bg-green-50 hover:bg-green-100'
                              : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                            title={player.confirmed ? 'Remove confirmation' : 'Mark as confirmed'}
                          >
                            {player.confirmed ? 'Confirmed ✓' : 'Confirm'}
                          </button>
                        )}
                        {player.original_photo_url && player.nobg_photo_url && (
                          <button
                            onClick={() => handleTogglePhoto(player)}
                            className="text-indigo-600 hover:text-indigo-800 text-xs border border-indigo-200 px-2 py-1 rounded"
                            title="Toggle View"
                          >
                            {player.photo_url === player.nobg_photo_url ? "Show Original" : "Show No-BG"}
                          </button>
                        )}
                        {player.upload_status && (
                          <>
                            <button
                              onClick={() => openLightbox(player)}
                              className="text-blue-600 hover:text-blue-800"
                              title="View"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => downloadPhoto(player)}
                              className="text-green-600 hover:text-green-800"
                              title="Download"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </button>
                          </>
                        )}
                        <label className="text-blue-600 hover:text-blue-800 cursor-pointer" title="Upload/Replace">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleReplacePhoto(e, player)}
                          />
                        </label>
                        {player.upload_status && (
                          <button
                            onClick={() => deletePhoto(player)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <LightboxModal
        show={showLightbox}
        image={lightboxImage}
        onClose={() => setShowLightbox(false)}
      />



      {/* Import the team's submitted list. Mounted only while open so the ExcelJS chunk
          is fetched on first use rather than with the page. */}
      {showImportRoster && (
        <ImportRosterModal
          team={team}
          existingPlayers={players}
          onCancel={() => setShowImportRoster(false)}
          onImport={handleRosterImport}
        />
      )}

      {/* Add Player / Search Modal */}
      <AddPlayerModal
        show={showAddPlayer}
        onClose={() => {
          setShowAddPlayer(false);
          setLinkPlayerQuery(''); // Clear the query when closing
          setLinkingPlayer(null); // Clear the linking player when closing
        }}
        initialQuery={linkPlayerQuery}
        onAddManualPlayer={handleAddManualPlayer}
        onConnectPlayer={handleConnectPlayer}
        eventCode={currentEventCode}
      />

      {/* Linking / Reconciliation Modal */}
      <PlayerLinkModal
        linkingData={linkingData}
        onConfirm={confirmLink}
        onCancel={() => setLinkingData(null)}
      />

      {/* Import Confirmation Modal */}
      <ImportConfirmModal
        data={importConfirmation}
        onConfirm={confirmImport}
        onCancel={() => setImportConfirmation(null)}
        onChange={setImportConfirmation}
      />

      {/* Smart Link Modal (Self-Healing) */}
      <SmartLinkModal
        candidate={smartLinkData?.candidate}
        remotePerson={smartLinkData?.remotePerson}
        onConfirmLink={handleConfirmSmartLink}
        onIgnore={handleIgnoreSmartLink}
        onCancel={() => setSmartLinkData(null)}
      />

      {/* Crop Modal for Sportradar Sync or Crop-Only */}
      <CropModal
        show={!!cropData}
        imageUrl={cropData?.imageUrl}
        playerName={cropData?.player?.player_name}
        onConfirm={cropData?.mode === 'cropOnly' ? handleCropConfirmOnly : handleCropConfirm}
        onCancel={() => setCropData(null)}
        title={cropData?.mode === 'cropOnly' ? 'Crop Photo' : 'Crop Photo for Sportradar'}
        subtitle={cropData?.mode === 'cropOnly'
          ? `${cropData?.player?.player_name || ''} - Drag to position, resize corners`
          : `${cropData?.player?.player_name || ''} - Drag to position, resize corners`
        }
        buttonText={cropData?.mode === 'cropOnly' ? 'Crop & Save' : 'Crop & Sync'}
      />

      {/* Accreditation Export Preview Modal (CSV + Photos) */}
      {csvPreviewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Export Preview</h3>
                <p className="text-sm text-gray-500 mt-1">Reviewing {csvPreviewData.playerData.length} records for {team.name} before zipping</p>
              </div>
              <button
                onClick={() => setCsvPreviewData(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6">
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 mb-6 flex gap-3">
                <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-amber-800">
                  <strong>Normalization Active:</strong> All names have been stripped of accents (e.g., François → Francois) for system compatibility. Emails are auto-generated from these normalized names.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold">
                    <tr>
                      {csvPreviewData.rows[0].map((header, i) => (
                        <th key={i} className="px-4 py-3">{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {csvPreviewData.rows.slice(1, 11).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        {row.map((cell, j) => (
                          <td key={j} className="px-4 py-3 text-gray-700">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {csvPreviewData.rows.length > 11 && (
                <p className="text-center text-gray-400 text-xs mt-4 font-medium italic">
                  Showing first 10 of {csvPreviewData.rows.length - 1} records...
                </p>
              )}
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
              <button
                onClick={() => setCsvPreviewData(null)}
                className="px-6 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmExportAccreditation}
                disabled={isExporting}
                className="px-8 py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isExporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Confirm & Download ZIP
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default AdminTeamDetail;
